// Leitner stats persistence — per-list stats slices under a single
// versioned key ("…stats.v2"), shape `{ [listId]: StatsMap }`. Switching
// decks swaps the active slice; each list keeps its own progression.
//
// The stats map is user-owned data; every parse path stays defensive so a
// corrupted entry never breaks boot.

import { STORAGE_KEYS } from "../config/storage-keys";
import { DEFAULT_LIST_ID } from "../config/lists";
import type { StatsMap } from "../types";

type StatsStore = Record<string, StatsMap>;

function readStore(ls: Storage): StatsStore {
  try {
    const raw = ls.getItem(STORAGE_KEYS.STATS);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as StatsStore;
  } catch {
    return {};
  }
}

function writeStore(store: StatsStore, ls: Storage): void {
  ls.setItem(STORAGE_KEYS.STATS, JSON.stringify(store));
}

/** One-shot migration of the flat v1 blob (single global StatsMap) into
 *  the v2 per-list store under DEFAULT_LIST_ID. Idempotent: the v1 key
 *  is removed on success, and a subsequent run is a no-op.
 *
 *  Silent when there's nothing to migrate (fresh install, or already on
 *  v2). We don't try to recover from a corrupted v1 blob — losing stats
 *  is preferable to importing garbage under DEFAULT_LIST_ID. */
export function migrateStatsV1ToV2(ls: Storage = localStorage): void {
  const legacy = ls.getItem(STORAGE_KEYS.STATS_V1);
  if (!legacy) return;
  try {
    const parsed = JSON.parse(legacy);
    if (parsed && typeof parsed === "object") {
      const store = readStore(ls);
      // Only populate the default slice if it's absent — a v2 slice that
      // already exists is strictly newer than any v1 blob.
      if (!store[DEFAULT_LIST_ID]) {
        store[DEFAULT_LIST_ID] = parsed as StatsMap;
        writeStore(store, ls);
      }
    }
  } catch {
    /* corrupted v1 — drop it */
  }
  ls.removeItem(STORAGE_KEYS.STATS_V1);
}

export function loadStats(
  listId: string,
  ls: Storage = localStorage,
): StatsMap {
  const store = readStore(ls);
  const slice = store[listId];
  return slice && typeof slice === "object" ? slice : {};
}

export function saveStats(
  listId: string,
  s: StatsMap,
  ls: Storage = localStorage,
): void {
  const store = readStore(ls);
  store[listId] = s;
  writeStore(store, ls);
}
